import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { token, type SpaceStep } from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Grid.module.css';

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  /** Fixed track count. Ignored when `minColumnWidth` is set. */
  columns?: number;
  /**
   * Responsive mode: `repeat(auto-fill, minmax(<min>, 1fr))`. Pass a token
   * (`token.metric.cardNodeW`, `token.layout.panelPalette`), not a raw length.
   */
  minColumnWidth?: string;
  gap?: SpaceStep;
  /** Column gap when it must differ from the row gap — a dense matrix. */
  columnGap?: SpaceStep;
  align?: 'start' | 'center' | 'end' | 'stretch';
  children: ReactNode;
}

const ALIGN = { start: 'start', center: 'center', end: 'end', stretch: 'stretch' } as const;

export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  {
    columns = 2,
    minColumnWidth,
    gap = 'sp05',
    columnGap,
    align = 'stretch',
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const vars: StyleWithVars = {
    '--dpem-grid-gap': token.space[gap],
    '--dpem-grid-col-gap': columnGap === undefined ? token.space[gap] : token.space[columnGap],
    '--dpem-grid-align': ALIGN[align],
    '--dpem-grid-template':
      minColumnWidth === undefined
        ? `repeat(${String(Math.max(1, columns))}, minmax(0, 1fr))`
        : `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
  };

  return (
    <div
      {...rest}
      ref={ref}
      className={clsx(styles.grid, className)}
      style={mergeStyle(vars, style)}
    >
      {children}
    </div>
  );
});
